import statusProcessing from "../assets/images/cases/status-processing.svg";
import statusCompleted from "../assets/images/cases/status-completed-correct.svg";
import statusAiAssessed from "../assets/icons/brain-status.svg";
import statusRejected from "../assets/images/cases/status-rejected.svg";
import statusUnderReview from "../assets/images/cases/status-under-review.svg";
import statusAwaitingRetry from "../assets/images/cases/status-awaiting-retry.svg";

// Returned when the claim state is genuinely unknown — distinct from any real
// state. Hegic's GET /assessment omits claim_state (it appears only in the list),
// and reporting that as "In-Process" or "AI Assessed" would state something false
// about the case rather than admitting the gap.
export const UNKNOWN_STATUS = {
  text: "Unknown",
  color: "#6b7280",
  bgColor: "transparent",
  icon: statusProcessing,
};

// Status mapping for assessment statuses
export const getStatusInfo = (status) => {
  // Guard before the switch: parseInt(undefined) is NaN, which would otherwise
  // fall through to default and read as a real state.
  // null and "" must be rejected explicitly — Number() coerces both to 0, which
  // is a real state (In-Process), so a finite check alone is not enough.
  if (status === null || status === undefined || status === "") {
    return UNKNOWN_STATUS;
  }
  const code = Number(status);
  if (!Number.isFinite(code)) return UNKNOWN_STATUS;

  switch (code) {
    case 0:
      return {
        text: "In-Process",
        color: "#1e2125",
        bgColor: "transparent",
        icon: statusProcessing,
      };
    case 1:
    case 2:
      return {
        text: "In-Process",
        color: "#1e2125",
        bgColor: "transparent",
        icon: statusProcessing,
      };
    case 3:
      return {
        text: "AI Assessed",
        color: "#36a149",
        bgColor: "transparent",
        icon: statusAiAssessed,
      };
    case 5:
      return {
        text: "Under Review",
        color: "#f8bc05",
        bgColor: "transparent",
        icon: statusUnderReview,
      };
    case 8:
      return {
        text: "Review Completed",
        color: "#36a149",
        bgColor: "transparent",
        icon: statusCompleted,
      };
    case 6:
      return {
        text: "Failed",
        color: "#df3822",
        bgColor: "transparent",
        icon: statusRejected,
      };
    case 7:
      return {
        text: "Awaiting Retry",
        color: "#ffaa76",
        bgColor: "rgba(0, 0, 0, 0.2)",
        icon: statusAwaitingRetry,
      };
    default:
      return {
        text: "In-Process",
        color: "#1e2125",
        bgColor: "transparent",
        icon: statusProcessing,
      };
  }
};

/**
 * Did this case fail?
 *
 * One place rather than `parseInt(x, 10) === 6` scattered about: 6 is the
 * backend's code and getStatusInfo above is where that is already written
 * down. Accepts the string the API sends as readily as a number.
 *
 * Used to hide the tabs that have nothing to say about a failed capture —
 * point of impact and the digital passport both describe damage on a vehicle
 * that was never successfully assessed.
 */
export const FAILED_CLAIM_STATE = 6;

export const isFailedStatus = (status) => {
  if (status === null || status === undefined || status === "") return false;
  const code = Number(status);
  return Number.isFinite(code) && code === FAILED_CLAIM_STATE;
};

/**
 * The backends here send "YYYY-MM-DD HH:MM:SS" — a space, not a "T". Chrome
 * parses that regardless; Safari returns Invalid Date from it (same gap
 * documented in defaultDateRange.js's parseCreatedAt, which this mirrors).
 * `new Date()` never throws on a bad string, so the try/catch below cannot
 * catch this on its own — Invalid Date silently stringifies to the literal
 * text "Invalid Date" unless this normalizes the separator first and checks
 * the result.
 */
const toSafeDate = (dateString) => {
  const date = new Date(String(dateString).trim().replace(" ", "T"));
  return Number.isNaN(date.getTime()) ? null : date;
};

// Format date for display
export const formatDate = (dateString) => {
  if (!dateString) return "-";

  try {
    const date = toSafeDate(dateString);
    if (!date) return dateString;
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch (error) {
    return dateString; // Return original if parsing fails
  }
};

// Format date and time for display
export const formatDateTime = (dateString) => {
  if (!dateString) return "-";

  try {
    const date = toSafeDate(dateString);
    if (!date) return dateString;
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch (error) {
    return dateString; // Return original if parsing fails
  }
};

// Format time for display
export const formatTime = (dateString) => {
  if (!dateString) return "-";

  try {
    const date = toSafeDate(dateString);
    if (!date) return dateString;
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (error) {
    return dateString; // Return original if parsing fails
  }
};

/**
 * getStatusInfo, with per-client wording.
 *
 * The same claim_state means different things to different backends: 3 is "AI
 * Assessed" in the motor lifecycle, and Hegic's own portal calls it
 * "Completed". A property inspection has no review stages at all, so its 3 is
 * simply done.
 *
 * Only the text is overridden — the colour and icon still come from the shared
 * map, so a state keeps looking like itself. Labels are declared per client
 * (`statusLabels` in src/config/clients/<key>.json), keyed by claim_state as a
 * string, and a state with no entry keeps the shared wording rather than
 * falling back to a blanket.
 */
export const getStatusInfoFor = (status, labels) => {
  const info = getStatusInfo(status);
  const override = labels?.[String(status)];
  return typeof override === "string" && override.trim()
    ? { ...info, text: override.trim() }
    : info;
};

// ---------------------------------------------------------------------------
// Clearline redesign helpers
//
// Used only by src/redesign/ (the side-by-side design comparison). Added here
// rather than duplicated because they are pure presentation helpers with no
// bearing on the existing design — nothing in the current UI imports them.
// ---------------------------------------------------------------------------

const CLEARLINE_STATUS_CHIPS = {
  "AI Assessed": { bg: "#e8edff", fg: "#2a4fd0" },
  "Under Review": { bg: "#fdf2dc", fg: "#9a6a10" },
  Completed: { bg: "#e3f4e8", fg: "#187a3c" },
  "Awaiting Retry": { bg: "#fdeadd", fg: "#b4551a" },
  Processing: { bg: "#eef0f3", fg: "#5b6572" },
  Rejected: { bg: "#fbe9e9", fg: "#c22f2f" },
};

const CLEARLINE_STATUS_FALLBACK = { bg: "#eef0f3", fg: "#5b6572" };

/** @returns {{bg: string, fg: string}} pill background + text/dot color. */
export const getClearlineStatusChip = (statusText) =>
  CLEARLINE_STATUS_CHIPS[statusText] || CLEARLINE_STATUS_FALLBACK;

/** Full timestamp, "yyyy-mm-dd hh:mm:ss" in local time, e.g. "2026-09-29 07:43:52". */
export const formatFullDateTime = (dateString) => {
  if (!dateString) return "-";

  const date = toSafeDate(dateString);
  if (!date) return dateString;

  const p = (n) => String(n).padStart(2, "0");
  return (
    `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())} ` +
    `${p(date.getHours())}:${p(date.getMinutes())}:${p(date.getSeconds())}`
  );
};

/**
 * Clearline "received" timestamp: "Today · 09:14", "Yesterday · 17:40",
 * "11 Aug · 10:21", falling back to "11 Aug 2025 · 10:21" for other years.
 */
export const formatClearlineDateTime = (dateString) => {
  if (!dateString) return "-";

  const date = toSafeDate(dateString);
  if (!date) return dateString;

  const time = date.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });

  const startOfDay = (d) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const now = new Date();
  const dayDelta = Math.round(
    (startOfDay(now) - startOfDay(date)) / 86400000,
  );

  if (dayDelta === 0) return `Today · ${time}`;
  if (dayDelta === 1) return `Yesterday · ${time}`;

  const day = date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: "numeric" }),
  });
  return `${day} · ${time}`;
};
